import type { FC, FocusEvent } from 'react';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import cn from 'classnames';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { Plate } from '@alfalab/core-components-plate';
import { Badge } from '@alfalab/core-components-badge';
import { AlertCircleMIcon } from '@alfalab/icons-glyph/AlertCircleMIcon';

import { ERROR, INN_TRUE, PENDING, SMS } from '../../config';
import { useFormContext } from '../../components/form/context';
import { validateForm } from '../../helpers/validators';
import { Captcha } from '../../components/captcha';

import type { TFormLayout } from './models';
import { FieldLabels } from './models/const';

export const FormLayout: FC<TFormLayout> = ({
    Agreement,
    Aligner,
    Button,
    CompanyFields,
    Email,
    FormInput,
    Grid,
    Phone,
    Safeguard,
    Text,
    isMobile,
    linkToCheckInn,
    locationPlaceholder,
    safeguardText,
    styles,
    textTermsUse,
}) => {
    const { fields, formId, innStatus, requestCode, status, updateStatus } = useFormContext();
    const form = useForm();
    const ref = useRef<HTMLDivElement>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const validateAllFields = useCallback(() => {
        const registeredFields = form.getRegisteredFields();
        registeredFields.forEach(field => {
            if (form.getFieldState(field)?.value) {
                form.blur(field);
            }
        });
    }, [form]);

    useEffect(() => {
        validateAllFields();
    }, [innStatus, validateAllFields, captchaToken]);

    useEffect(() => {
        if (ref.current) {
            // Задаем  ограничение на количество символов в jQuery стиле
            // К сожалению, стандартных возможностей у виджета Email для такого функционала нет
            const emailInputEl = ref.current.querySelector('[data-test-id="email-input"]');
            emailInputEl?.setAttribute('maxlength', '70');
        }
        // eslint-disable-next-line
    }, []);

    const handleContinue = async () => {
        const isValid = validateForm(form);

        sendEvent({
            event: 'site_formRequestButton_click',
            page: window.document.title,
            url: window.location.href,
            form_id: formId,
            form_with_inn: innStatus === INN_TRUE,
        });

        if (isValid()) {
            try {
                updateStatus(PENDING);
                await requestCode(form.getState().values.phone, captchaToken);
                sendEvent({ event: 'site_formInputCode_check', result: '200 Ok' });
                updateStatus(SMS);
            } catch (error) {
                const err = error as { response: { status: number } };

                if (err?.response?.status === 424) {
                    // Не отправляем форму если токен капчи невалиден
                    updateStatus(ERROR);
                } else {
                    sendEvent({ event: 'site_formInputCode_check', result: '4xx/5xx' });
                    // отправляем форму без подтверждения телефона. требование бизнеса
                    console.info('Форма отправлена без подтверждения телефона', error);
                    form.submit();
                }
            }
        } else {
            // если форма не валидна то запускаем отправку чтобы показались ошибки. Другого варианта показать ошибки не нашел
            form.submit();
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (e.target?.name) {
            sendEvent({
                event: 'site_formRequestField',
                name: FieldLabels[e.target?.name],
                content: e.target?.value,
                form_id: formId,
            });
        }
    };

    const renderSubmitBtn = () => {
        return (
            <Aligner align="center" styler={{ marginTop: 24 }}>
                <Button
                    block={isMobile}
                    disabled={status === PENDING || !captchaToken}
                    innerClassName={cn(styles.external, styles.button)}
                    onClick={handleContinue}
                    size="s"
                    view="primary"
                >
                    {status === PENDING ? 'Отправляем' : 'Отправить'}
                </Button>
            </Aligner>
        );
    };

    const rowStyle = cn(styles.external, styles.row);
    const gridStyles = {
        [isMobile ? 'col' : 'grid']: rowStyle,
    };

    return (
        <div ref={ref} onBlur={handleBlur}>
            {isMobile && (
                <>
                    <Text
                        className={cn(styles.external, styles.header)}
                        fontPreset="systemNormal"
                        tag="h2"
                    >
                        Заявка на оформление лизинга
                    </Text>
                    <Safeguard text={safeguardText} />
                </>
            )}
            {fields?.includes('inn') && (
                <CompanyFields
                    linkToCheckInn={linkToCheckInn}
                    locationPlaceholder={locationPlaceholder}
                    rowStyle={rowStyle}
                />
            )}
            {!fields?.includes('inn') && (
                <Aligner align="left" styler={{ marginTop: 24 }}>
                    <Plate
                        className={cn(styles.external, styles.plate)}
                        leftAddons={
                            <Badge
                                content={<AlertCircleMIcon />}
                                iconColor="secondary"
                                view="icon"
                            />
                        }
                        title="Работаем только с юридическими лицами и ИП"
                        titleView="light"
                        view="common"
                    />
                </Aligner>
            )}
            {fields?.includes('fio') && (
                <FormInput
                    className={cn(styles.external, styles.row)}
                    isRequired
                    name="fio"
                    settings={{
                        caption: 'Укажите точно как в паспорте',
                        placeholder: 'ФИО контактного лица',
                        accept: '^[а-яА-яёЁ\\s\\-]*$',
                        maxLength: 200,
                    }}
                />
            )}
            <Grid classNames={gridStyles} vertical={isMobile}>
                {fields?.includes('phone') && (
                    <Phone
                        name="phone"
                        settings={{
                            caption: 'Отправим проверочный код',
                            placeholder: 'Мобильный телефон',
                        }}
                    />
                )}
                {fields?.includes('email') && (
                    <Email
                        name="email"
                        settings={{
                            caption: 'Направим информацию по заявке',
                            placeholder: 'Электронная почта',
                        }}
                    />
                )}
            </Grid>
            <div style={{ height: 24 }} />
            <Captcha onSuccess={setCaptchaToken} onTokenExpired={() => setCaptchaToken(null)} />
            {isMobile ? (
                <>
                    {renderSubmitBtn()}
                    <Agreement textTermsUse={textTermsUse} />
                </>
            ) : (
                <>
                    <Agreement textTermsUse={textTermsUse} />
                    {renderSubmitBtn()}
                </>
            )}
        </div>
    );
};
